/* eslint-disable lines-between-class-members */
import { ScriptableObject, ScriptableObjectMixin, ScriptableObjects } from "./Base.js";

const ZONE_PROPS = [
  // audio-zone specific
  "enabled",
  "inOut",
  "outIn",
  "target",
  // audio params
  "audioType",
  "gain",
  "distanceModel",
  "rolloffFactor",
  "refDistance",
  "maxDistance",
  "coneInnerAngle",
  "coneOuterAngle",
  "coneOuterGain"
];

// Utility function to walk up prototype chain and find property descriptors
function findPropertyDescriptor(obj, prop) {
  let current = obj;
  while (current) {
    const descriptor = Object.getOwnPropertyDescriptor(current, prop);
    if (descriptor) return descriptor;
    current = Object.getPrototypeOf(current);
  }
  return null;
}

/* ---------------------- Editor adapter ---------------------- */
class AudioZoneEditorAdapter {
  constructor(sourceObject, originalDescriptors) {
    this.sourceObject = sourceObject;
    this._values = new Map();
    this._originalDescriptors = originalDescriptors;

    ZONE_PROPS.forEach(prop => {
      if (prop in this) return;

      const descriptor = originalDescriptors.get(prop);
      let initialValue;
      try {
        if (descriptor && descriptor.get) {
          initialValue = descriptor.get.call(sourceObject);
        } else if (Object.prototype.hasOwnProperty.call(sourceObject, prop)) {
          initialValue = sourceObject[prop];
        }
      } catch (e) {
        initialValue = undefined;
      }
      this._values.set(prop, initialValue);

      Object.defineProperty(this, prop, {
        get: () => this._values.get(prop),
        set: v => {
          this._values.set(prop, v);
          if (!this.sourceObject) return;

          const originalDescriptor = this._originalDescriptors.get(prop);
          if (originalDescriptor && originalDescriptor.set) {
            originalDescriptor.set.call(this.sourceObject, v);
          } else {
            this.sourceObject[prop] = v;
          }
        }
      });
    });
  }

  dispose() {
    this._values.clear();
    this.sourceObject = null;
  }
}

/* ---------------------- A-Frame adapter --------------------- */
class AudioZoneAFrameAdapter {
  constructor(sourceObject) {
    this.sourceObject = sourceObject;
    const ZONE = "audio-zone";
    const PARAMS = "audio-params";
    const _pendingUpdates = {};
    this._pendingUpdates = _pendingUpdates;

    const _flushPending = () => {
      if (!this.sourceObject?.el) return;
      if (_pendingUpdates[ZONE]) {
        const current = {
          ...(this.sourceObject.el.getAttribute(ZONE) || {})
        };
        Object.assign(current, _pendingUpdates[ZONE]);
        this.sourceObject.el.setAttribute(ZONE, current);
        delete _pendingUpdates[ZONE];
      }
      if (_pendingUpdates[PARAMS]) {
        const current = {
          ...(this.sourceObject.el.getAttribute(PARAMS) || {})
        };
        Object.assign(current, _pendingUpdates[PARAMS]);
        this.sourceObject.el.setAttribute(PARAMS, current);
        delete _pendingUpdates[PARAMS];
      }
    };

    if (this.sourceObject?.el) {
      this.sourceObject.el.addEventListener("componentinitialized", e => {
        if (e.detail.name === ZONE || e.detail.name === PARAMS) {
          _flushPending();
        }
      });
    }

    const setAttr = (component, key, val) => {
      if (!this.sourceObject?.el) return;
      const attr = this.sourceObject.el.getAttribute(component);
      if (!attr) {
        if (!_pendingUpdates[component]) _pendingUpdates[component] = {};
        _pendingUpdates[component][key] = val;
      } else {
        const current = { ...attr };
        current[key] = val;
        this.sourceObject.el.setAttribute(component, current);
      }
    };
    const getAttr = (component, key) => {
      const attr = this.sourceObject?.el?.getAttribute(component);
      if (attr) return attr[key];
      return _pendingUpdates[component]?.[key];
    };

    // audio-zone mapping
    ["enabled", "inOut", "outIn", "target"].forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getAttr(ZONE, prop),
        set: v => setAttr(ZONE, prop, v)
      });
    });

    // audio-params mapping
    [
      "audioType",
      "gain",
      "distanceModel",
      "rolloffFactor",
      "refDistance",
      "maxDistance",
      "coneInnerAngle",
      "coneOuterAngle",
      "coneOuterGain"
    ].forEach(prop => {
      Object.defineProperty(this, prop, {
        get: () => getAttr(PARAMS, prop),
        set: v => setAttr(PARAMS, prop, v)
      });
    });
  }

  dispose() {
    this.sourceObject = null;
  }
}

/* -------------------------------------------------------------------------- */
/*  AFrameAudioZoneWrapper - A-Frame implementation                         */
/* -------------------------------------------------------------------------- */
class AFrameAudioZoneWrapper extends ScriptableObject {
  constructor(el, initial = {}) {
    super();
    this.scriptableType = "audio-zone";
    this.el = el;
    this._adapter = new AudioZoneAFrameAdapter(this);

    Object.keys(initial).forEach(key => {
      if (key in this) this[key] = initial[key];
    });
    this.updateMatrixWorld(true);
  }

  clone(recursive = true) {
    const cloneInitial = {};
    ZONE_PROPS.forEach(prop => {
      const value = this[prop];
      if (value !== undefined) cloneInitial[prop] = value;
    });
    // eslint-disable-next-line no-use-before-define
    const cloned = createAudioZoneElementAFrame(cloneInitial);
    cloned.copy(this, recursive);
    return cloned;
  }
}

ZONE_PROPS.forEach(prop => {
  Object.defineProperty(AFrameAudioZoneWrapper.prototype, prop, {
    get() {
      return this._adapter?.[prop];
    },
    set(v) {
      if (this._adapter) this._adapter[prop] = v;
    },
    enumerable: true,
    configurable: true
  });
});

/* -------------------------------------------------------------------------- */
/*  Factory functions                                                          */
/* -------------------------------------------------------------------------- */

function createAudioZoneElementAFrame(initial = {}) {
  if (typeof document === "undefined") {
    console.error("Cannot create AudioZoneElementAFrame in non-browser environment");
    return {};
  }

  const el = document.createElement("a-entity");
  el.setAttribute("audio-zone", {
    enabled: true,
    inOut: true,
    outIn: true,
    target: null,
    ...initial
  });
  el.setAttribute("audio-params", {
    audioType: "zone",
    gain: 1,
    distanceModel: "inverse",
    rolloffFactor: 1,
    refDistance: 1,
    maxDistance: 100,
    coneInnerAngle: 360,
    coneOuterAngle: 0,
    coneOuterGain: 0,
    ...initial
  });

  return new AFrameAudioZoneWrapper(el, initial);
}

function makeEditorAudioZoneNodeScriptable(node) {
  if (node.isScriptableObject) return node;

  const Ctor = node.constructor;
  const ScriptableClass = ScriptableObjectMixin(Ctor, "AudioZoneElement");
  Object.setPrototypeOf(node, ScriptableClass.prototype);

  node.isScriptableObject = true;
  node._transformSyncSetup = false;
  node.scriptableType = "audio-zone";

  const originalDescriptors = new Map();
  ZONE_PROPS.forEach(prop => {
    const descriptor = findPropertyDescriptor(node, prop);
    originalDescriptors.set(prop, descriptor);
  });

  node._adapter = new AudioZoneEditorAdapter(node, originalDescriptors);

  ZONE_PROPS.forEach(prop => {
    Object.defineProperty(node, prop, {
      get() {
        return this._adapter?.[prop];
      },
      set(v) {
        if (this._adapter) this._adapter[prop] = v;
      },
      enumerable: true,
      configurable: true
    });
  });

  if (!ScriptableClass.prototype.hasOwnProperty("clone")) {
    const originalClone = Ctor.prototype.clone;
    ScriptableClass.prototype.clone = function (recursive = true) {
      const cloned = originalClone.call(this, recursive);
      return makeEditorAudioZoneNodeScriptable(cloned);
    };
  }

  return node;
}

function createAudioZoneElementEditor(initial = {}) {
  /* eslint-disable no-undef */
  const NodeCtor =
    (typeof globalThis !== "undefined" && (globalThis.AudioZoneNode || globalThis.Object3D)) || undefined;

  if (typeof NodeCtor !== "function") {
    throw new Error("AudioZoneElementEditor: AudioZoneNode not found");
  }

  const editor = ScriptableObject.editor;
  if (!editor) {
    throw new Error("AudioZoneElementEditor: ScriptableObject.editor not set");
  }

  let node;
  try {
    node = new NodeCtor(editor);
  } catch (e) {
    throw new Error(`AudioZoneElementEditor: failed to construct AudioZoneNode: ${e?.message || e}`);
  }

  const scriptableNode = makeEditorAudioZoneNodeScriptable(node);

  Object.keys(initial).forEach(key => {
    if (key in scriptableNode) scriptableNode[key] = initial[key];
  });

  return scriptableNode;
}

/* -------------------------------------------------------------------------- */
/*  AudioZoneElement - Main factory class                                     */
/* -------------------------------------------------------------------------- */

export class AudioZoneElement {
  constructor(initial = {}) {
    const runtimeMode = ScriptableObject.runtimeMode || "editor";

    if (runtimeMode === "aframe") {
      return createAudioZoneElementAFrame(initial);
    } else {
      return createAudioZoneElementEditor(initial);
    }
  }

  static setEditor(editor) {
    ScriptableObject.editor = editor;
  }
}

// Register factory so scene.getObjectByName wraps automatically
ScriptableObjects.registerType(
  "audio-zone",
  sourceObject => {
    if (ScriptableObject.runtimeMode === "aframe") {
      return new AFrameAudioZoneWrapper(sourceObject.el);
    }
    return makeEditorAudioZoneNodeScriptable(sourceObject);
  },
  sourceObject => {
    const runtimeMode = ScriptableObject.runtimeMode || "editor";
    if (runtimeMode === "aframe") {
      return sourceObject.hasAttribute?.("audio-zone") && !sourceObject.scriptableObject;
    }

    /* eslint-disable no-undef */
    const NodeCtor =
      (typeof globalThis !== "undefined" && (globalThis.AudioZoneNode || globalThis.Object3D)) || undefined;
    if (!NodeCtor) return false;
    return sourceObject instanceof NodeCtor && !sourceObject.isScriptableObject;
  }
);

export { AudioZoneAFrameAdapter, AudioZoneEditorAdapter, ZONE_PROPS };
